import { env, pipeline } from '@xenova/transformers';
import { addRxPlugin, createRxDatabase } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';

import type { RxCollection, RxDatabase, RxDocument } from 'rxdb';

// Add dev-mode plugin based on environment
// For debugging in demo/staging, you can set REACT_APP_ENABLE_RXDB_DEBUG=true
const enableDebug =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ENABLE_RXDB_DEBUG === 'true' ||
  process.env.REACT_APP_ENV === 'demo';

if (enableDebug) {
  // Dynamic import to avoid bundling when not needed
  import('rxdb/plugins/dev-mode')
    .then(module => {
      addRxPlugin(module.RxDBDevModePlugin);
      console.log(
        'RxDB dev-mode plugin loaded for environment:',
        process.env.REACT_APP_ENV || process.env.NODE_ENV,
      );
    })
    .catch(err => {
      console.error('Failed to load RxDB dev-mode plugin:', err);
    });
}

// Configure transformers to use remote models only (no local cache)
env.allowLocalModels = false;

export interface ZoomRecordingDoc {
  id: string;
  recordingId: string;
  topic: string;
  startTime: string;
  duration: number;
  transcript?: string;
  transcriptChunks?: ArrayLike<string>;
  embedding?: ArrayLike<number>;
  chunkEmbeddings?: ArrayLike<ArrayLike<number>>;
  hasVideo: boolean;
  hasTranscript: boolean;
  shareUrl?: string;
  metadata?: any;
}

export type ZoomRecordingCollection = RxCollection<ZoomRecordingDoc>;
export type ZoomRecordingDocument = RxDocument<ZoomRecordingDoc>;

export interface VectorDatabase {
  recordings: ZoomRecordingCollection;
  embeddings: RxCollection<{
    id: string;
    recordingId: string;
    chunkIndex: number;
    text: string;
    embedding: number[];
  }>;
}

let db: RxDatabase<VectorDatabase> | null = null;
let embeddingPipeline: any = null;

// Initialize the embedding model
async function getEmbeddingModel() {
  if (!embeddingPipeline) {
    console.log('Initializing embedding model...');
    embeddingPipeline = await pipeline(
      'feature-extraction',
      'Xenova/all-MiniLM-L6-v2',
    );
  }
  return embeddingPipeline;
}

// Generate embeddings for text
export async function generateEmbedding(text: string): Promise<number[]> {
  const model = await getEmbeddingModel();
  const output = await model(text, {
    pooling: 'mean',
    normalize: true,
  });
  return Array.from(output.data);
}

// Calculate cosine similarity between two vectors
function cosineSimilarity(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// Split transcript into chunks for better search
function chunkTranscript(transcript: string, chunkSize = 500): string[] {
  const words = transcript.split(' ');
  const chunks: string[] = [];

  for (let i = 0; i < words.length; i += chunkSize) {
    const chunk = words.slice(i, i + chunkSize).join(' ');
    if (chunk.trim().length > 0) {
      chunks.push(chunk);
    }
  }

  return chunks;
}

// Initialize or get the database
export async function getVectorDB(): Promise<RxDatabase<VectorDatabase>> {
  if (!db) {
    console.log('Creating RxDB database...');
    console.log('Environment:', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENV: process.env.REACT_APP_ENV,
      REACT_APP_ENABLE_RXDB_DEBUG: process.env.REACT_APP_ENABLE_RXDB_DEBUG,
    });

    // Use validated storage when debugging is enabled
    const enableDebug =
      process.env.NODE_ENV !== 'production' ||
      process.env.REACT_APP_ENABLE_RXDB_DEBUG === 'true' ||
      process.env.REACT_APP_ENV === 'demo';

    // For demo environment, you might want to disable validation if it's causing issues
    const useValidation = enableDebug && process.env.REACT_APP_ENV !== 'demo';

    const storage = useValidation
      ? wrappedValidateAjvStorage({ storage: getRxStorageDexie() })
      : getRxStorageDexie();

    console.log(
      'RxDB storage mode:',
      enableDebug ? 'validated (debug)' : 'production',
    );

    try {
      db = await createRxDatabase<VectorDatabase>({
        name: 'zoom_vector_db',
        storage: storage,
        ignoreDuplicate: true,
      });
    } catch (error) {
      console.error('Failed to create RxDB database:', error);
      console.error('Error details:', {
        message: (error as any).message,
        code: (error as any).code,
        stack: (error as any).stack,
      });
      throw error;
    }

    // Create recordings collection
    try {
      const collections = await db.addCollections({
        recordings: {
          schema: {
            version: 0,
            primaryKey: 'id',
            type: 'object',
            properties: {
              id: {
                type: 'string',
                maxLength: 100,
              },
              recordingId: {
                type: 'string',
              },
              topic: {
                type: 'string',
              },
              startTime: {
                type: 'string',
              },
              duration: {
                type: 'number',
              },
              transcript: {
                type: 'string',
              },
              transcriptChunks: {
                type: 'array',
                items: {
                  type: 'string',
                },
              },
              embedding: {
                type: 'array',
                items: {
                  type: 'number',
                },
              },
              chunkEmbeddings: {
                type: 'array',
                items: {
                  type: 'array',
                  items: {
                    type: 'number',
                  },
                },
              },
              hasVideo: {
                type: 'boolean',
              },
              hasTranscript: {
                type: 'boolean',
              },
              shareUrl: {
                type: 'string',
              },
              metadata: {
                type: 'object',
              },
            },
            required: [
              'id',
              'recordingId',
              'topic',
              'startTime',
              'duration',
              'hasVideo',
              'hasTranscript',
            ],
          },
        },
        embeddings: {
          schema: {
            version: 0,
            primaryKey: 'id',
            type: 'object',
            properties: {
              id: {
                type: 'string',
                maxLength: 100,
              },
              recordingId: {
                type: 'string',
              },
              chunkIndex: {
                type: 'number',
              },
              text: {
                type: 'string',
              },
              embedding: {
                type: 'array',
                items: {
                  type: 'number',
                },
              },
            },
            required: ['id', 'recordingId', 'chunkIndex', 'text', 'embedding'],
          },
        },
      });

      console.log('Database collections created:', Object.keys(collections));
    } catch (error) {
      console.error('Failed to create collections:', error);
      console.error('Collection creation error:', {
        message: (error as any).message,
        code: (error as any).code,
        rxError: (error as any).rxdb,
        parameters: (error as any).parameters,
      });
      throw error;
    }
  }

  if (!db) {
    throw new Error('Failed to initialize database');
  }

  return db;
}

// Add or update a recording with transcript
export async function addRecording(
  recording: any,
  transcript?: string,
): Promise<void> {
  try {
    const db = await getVectorDB();

    if (!db || !db.recordings) {
      console.error('Database or recordings collection not initialized');
      throw new Error('Database not ready');
    }

    // Validate required fields first
    if (
      !recording.id ||
      !recording.topic ||
      recording.start_time === undefined ||
      recording.duration === undefined ||
      recording.has_video === undefined ||
      recording.has_transcript === undefined
    ) {
      console.error('Missing required fields in recording:', {
        id: recording.id,
        topic: recording.topic,
        start_time: recording.start_time,
        duration: recording.duration,
        has_video: recording.has_video,
        has_transcript: recording.has_transcript,
      });
      throw new Error('Recording is missing required fields');
    }

    // Ensure ID is safe for database (should already be sanitized, but double-check)
    const safeId = recording.id.replace(/[^a-zA-Z0-9-_]/g, '_');

    // Prepare searchable text
    const searchableText = `${recording.topic} ${transcript || ''}`.trim();

    let embedding: number[] = [];
    let transcriptChunks: string[] = [];
    let chunkEmbeddings: number[][] = [];

    // Generate embeddings if we have content
    if (searchableText.length > 0) {
      // Generate overall embedding for the recording
      embedding = await generateEmbedding(searchableText.slice(0, 1000)); // Limit for performance

      // If we have a transcript, chunk it and generate embeddings for chunks
      if (transcript && transcript.length > 0) {
        transcriptChunks = chunkTranscript(transcript);

        // Generate embeddings for each chunk
        for (let i = 0; i < transcriptChunks.length; i++) {
          const chunkEmbedding = await generateEmbedding(transcriptChunks[i]);
          chunkEmbeddings.push(chunkEmbedding);

          // Also store in embeddings collection for better search
          if (db.embeddings) {
            await db.embeddings.upsert({
              id: `${safeId}_chunk_${i}`,
              recordingId: safeId,
              chunkIndex: i,
              text: transcriptChunks[i],
              embedding: chunkEmbedding,
            });
          }
        }
      }
    }

    // Log the data we're about to upsert for debugging
    const upsertData = {
      id: safeId,
      recordingId: safeId,
      topic: recording.topic || 'Untitled',
      startTime: recording.start_time || new Date().toISOString(),
      duration: recording.duration || 0,
      transcript: transcript || '',
      transcriptChunks: transcriptChunks || [],
      embedding: embedding || [],
      chunkEmbeddings: chunkEmbeddings || [],
      hasVideo: Boolean(recording.has_video),
      hasTranscript: Boolean(recording.has_transcript),
      shareUrl: recording.share_url || '',
      metadata: {
        totalSize: recording.total_size || 0,
        recordingCount: recording.recording_count || 0,
        password: recording.password || '',
        recordingFiles: recording.recording_files || [],
      },
    };

    console.log('Upserting recording with data:', {
      id: upsertData.id,
      topic: upsertData.topic,
      hasVideo: upsertData.hasVideo,
      hasTranscript: upsertData.hasTranscript,
    });

    // Upsert the recording
    await db.recordings.upsert(upsertData);
  } catch (error) {
    console.error('Error adding recording to vector DB:', error);
    console.error('Recording that caused error:', recording);
    throw error;
  }
}

// Helper function to highlight matching text
function getHighlightedSnippet(
  text: string,
  query: string,
  maxLength = 200,
): string {
  if (!text) return '';

  // Find the best matching portion
  const queryWords = query.toLowerCase().split(/\s+/);
  const textLower = text.toLowerCase();

  // Find the position with most query words nearby
  let bestPosition = 0;
  let bestScore = 0;

  for (let i = 0; i < text.length - 100; i += 50) {
    const snippet = textLower.substring(i, Math.min(i + 200, text.length));
    let score = 0;
    queryWords.forEach(word => {
      if (snippet.includes(word)) score++;
    });
    if (score > bestScore) {
      bestScore = score;
      bestPosition = i;
    }
  }

  // Extract snippet around best position
  const start = Math.max(0, bestPosition);
  const end = Math.min(text.length, start + maxLength);
  let snippet = text.substring(start, end);

  // Add ellipsis if needed
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';

  return snippet;
}

// Perform vector search
export async function vectorSearch(
  query: string,
  limit = 10,
): Promise<
  Array<{
    recording: ZoomRecordingDoc;
    score: number;
    matchedChunk?: string;
    highlightedSnippet?: string;
  }>
> {
  const db = await getVectorDB();

  // Generate query embedding
  const queryEmbedding = await generateEmbedding(query);

  // Get all recordings
  const recordings = await db.recordings.find().exec();

  // Calculate similarity scores
  const results = recordings.map(doc => {
    const recordingData = doc.toJSON();
    let maxScore = 0;
    let matchedChunk: string | undefined;
    let highlightedSnippet: string | undefined;

    // Check overall embedding similarity
    if (recordingData.embedding && recordingData.embedding.length > 0) {
      maxScore = cosineSimilarity(queryEmbedding, recordingData.embedding);
    }

    // Check chunk embeddings for better match
    if (recordingData.chunkEmbeddings && recordingData.transcriptChunks) {
      for (let i = 0; i < recordingData.chunkEmbeddings.length; i++) {
        const chunkScore = cosineSimilarity(
          queryEmbedding,
          recordingData.chunkEmbeddings[i],
        );
        if (chunkScore > maxScore) {
          maxScore = chunkScore;
          matchedChunk = recordingData.transcriptChunks[i];
          highlightedSnippet = getHighlightedSnippet(matchedChunk, query);
        }
      }
    }

    // If no chunk match but we have a transcript, create a snippet
    if (!highlightedSnippet && recordingData.transcript && maxScore > 0.3) {
      highlightedSnippet = getHighlightedSnippet(
        recordingData.transcript,
        query,
      );
    }

    return {
      recording: recordingData,
      score: maxScore,
      matchedChunk,
      highlightedSnippet,
    };
  });

  // Sort by score and return top results
  return results
    .filter(r => r.score > 0.3) // Filter out low relevance results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// Hybrid search combining vector and text search
export async function hybridSearch(
  query: string,
  limit = 10,
): Promise<
  Array<{
    recording: ZoomRecordingDoc;
    score: number;
    matchedChunk?: string;
    highlightedSnippet?: string;
  }>
> {
  const db = await getVectorDB();

  // Perform vector search
  const vectorResults = await vectorSearch(query, limit * 2);

  // Perform text search
  const textQuery = query.toLowerCase();
  const textResults = await db.recordings
    .find({
      selector: {
        $or: [
          { topic: { $regex: textQuery, $options: 'i' } },
          { transcript: { $regex: textQuery, $options: 'i' } },
        ],
      },
    })
    .exec();

  // Combine results with weighted scores
  const combinedResults = new Map<
    string,
    {
      recording: ZoomRecordingDoc;
      score: number;
      matchedChunk?: string;
      highlightedSnippet?: string;
    }
  >();

  // Add vector search results
  vectorResults.forEach(result => {
    combinedResults.set(result.recording.id, {
      ...result,
      score: result.score * 0.7, // Weight for semantic similarity
    });
  });

  // Add/update with text search results
  textResults.forEach(doc => {
    const recordingData = doc.toJSON();
    const existing = combinedResults.get(recordingData.id);
    const textScore = 0.3; // Base score for text match

    if (existing) {
      existing.score += textScore;
    } else {
      const highlightedSnippet = recordingData.transcript
        ? getHighlightedSnippet(recordingData.transcript, query)
        : undefined;

      combinedResults.set(recordingData.id, {
        recording: recordingData,
        score: textScore,
        matchedChunk: undefined,
        highlightedSnippet,
      });
    }
  });

  // Sort by combined score and return top results
  return Array.from(combinedResults.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// Get list of already indexed recording IDs
export async function getIndexedRecordingIds(): Promise<Set<string>> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      return new Set();
    }

    const recordings = await db.recordings.find().exec();
    const ids = new Set(recordings.map(r => r.recordingId));
    console.log(`Found ${ids.size} indexed recordings in database`);
    return ids;
  } catch (error) {
    console.error('Error getting indexed recordings:', error);
    return new Set();
  }
}

// Check if a specific recording is already indexed
export async function isRecordingIndexed(
  recordingId: string,
): Promise<boolean> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      return false;
    }

    const recording = await db.recordings.findOne(recordingId).exec();
    return !!recording;
  } catch (error) {
    console.error('Error checking if recording indexed:', error);
    return false;
  }
}

// Get all indexed recordings from the database
export async function getAllIndexedRecordings(): Promise<any[]> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      return [];
    }

    const recordings = await db.recordings.find().exec();

    // Convert to plain objects matching the ZoomRecording interface
    return recordings
      .map(r => ({
        id: r.recordingId,
        uuid: r.recordingId, // Using same as id since we don't store original UUID
        topic: r.topic,
        start_time: r.startTime,
        duration: r.duration,
        total_size: r.metadata?.totalSize || 0,
        recording_count: r.metadata?.recordingCount || 0,
        share_url: r.shareUrl || '',
        has_video: r.hasVideo,
        has_transcript: r.hasTranscript,
        password: r.metadata?.password || '',
        recording_files: r.metadata?.recordingFiles || [],
      }))
      .sort((a, b) => {
        // Sort by date, newest first
        return (
          new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
        );
      });
  } catch (error) {
    console.error('Error getting all indexed recordings:', error);
    return [];
  }
}

// Update an existing recording (e.g., if transcript is newly available)
export async function updateRecording(
  recordingId: string,
  transcript?: string,
): Promise<void> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      console.error('Database not initialized');
      return;
    }

    const existing = await db.recordings.findOne(recordingId).exec();
    if (!existing) {
      console.log(`Recording ${recordingId} not found for update`);
      return;
    }

    // Generate new embeddings if transcript provided
    if (transcript && transcript.length > 0) {
      const searchableText = `${existing.topic} ${transcript}`.trim();
      const embedding = await generateEmbedding(searchableText.slice(0, 1000));
      const chunks = chunkTranscript(transcript);
      const chunkEmbeddings: number[][] = [];

      for (const chunk of chunks) {
        const chunkEmbedding = await generateEmbedding(chunk);
        chunkEmbeddings.push(chunkEmbedding);
      }

      await existing.patch({
        transcript,
        transcriptChunks: chunks,
        embedding,
        chunkEmbeddings,
      });

      console.log(`Updated recording ${recordingId} with transcript`);
    }
  } catch (error) {
    console.error('Error updating recording:', error);
  }
}

// Remove outdated recordings that are no longer in the list
export async function removeOutdatedRecordings(
  currentRecordingIds: Set<string>,
): Promise<void> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      return;
    }

    const allRecordings = await db.recordings.find().exec();
    const toRemove = allRecordings.filter(
      r => !currentRecordingIds.has(r.recordingId),
    );

    if (toRemove.length > 0) {
      console.log(`Removing ${toRemove.length} outdated recordings`);
      for (const recording of toRemove) {
        await recording.remove();
      }
    }
  } catch (error) {
    console.error('Error removing outdated recordings:', error);
  }
}

// Get database statistics with size estimates
export async function getDatabaseStats(): Promise<{
  totalRecordings: number;
  recordingsWithTranscripts: number;
  totalEmbeddings: number;
  estimatedSizeMB: number;
  transcriptCharCount: number;
  averageTranscriptLength: number;
}> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings) {
      return {
        totalRecordings: 0,
        recordingsWithTranscripts: 0,
        totalEmbeddings: 0,
        estimatedSizeMB: 0,
        transcriptCharCount: 0,
        averageTranscriptLength: 0,
      };
    }

    const recordings = await db.recordings.find().exec();
    const embeddings = await db.embeddings.find().exec();

    const recordingsWithTranscripts = recordings.filter(
      r => r.transcript && r.transcript.length > 0,
    );

    // Calculate total transcript characters
    let transcriptCharCount = 0;
    recordingsWithTranscripts.forEach(r => {
      transcriptCharCount += r.transcript?.length || 0;
    });

    // Estimate database size (rough calculation)
    // Each embedding is ~384 floats * 4 bytes = ~1.5KB
    // Plus metadata, transcript text, etc.
    const embeddingSize = embeddings.length * 1.5; // KB
    const transcriptSize = transcriptCharCount / 1024; // KB
    const metadataSize = recordings.length * 0.5; // KB per recording
    const estimatedSizeKB = embeddingSize + transcriptSize + metadataSize;
    const estimatedSizeMB = Math.round((estimatedSizeKB / 1024) * 100) / 100;

    const averageTranscriptLength =
      recordingsWithTranscripts.length > 0
        ? Math.round(transcriptCharCount / recordingsWithTranscripts.length)
        : 0;

    return {
      totalRecordings: recordings.length,
      recordingsWithTranscripts: recordingsWithTranscripts.length,
      totalEmbeddings: embeddings.length,
      estimatedSizeMB,
      transcriptCharCount,
      averageTranscriptLength,
    };
  } catch (error) {
    console.error('Error getting database stats:', error);
    return {
      totalRecordings: 0,
      recordingsWithTranscripts: 0,
      totalEmbeddings: 0,
      estimatedSizeMB: 0,
      transcriptCharCount: 0,
      averageTranscriptLength: 0,
    };
  }
}

// Clear vector database with optional filtering
export async function clearVectorDB(options?: {
  onlyTranscripts?: boolean;
  olderThanDays?: number;
}): Promise<void> {
  try {
    const db = await getVectorDB();
    if (!db || !db.recordings || !db.embeddings) {
      console.error('Database or collections not initialized');
      return;
    }

    if (options?.onlyTranscripts) {
      // Clear only transcript data to save space
      const recordings = await db.recordings.find().exec();
      for (const recording of recordings) {
        await recording.patch({
          transcript: '',
          transcriptChunks: [],
          embedding: [],
          chunkEmbeddings: [],
        });
      }
      await db.embeddings.find().remove();
      console.log('Cleared transcript data from vector database');
    } else if (options?.olderThanDays) {
      // Clear recordings older than specified days
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - options.olderThanDays);
      const recordings = await db.recordings.find().exec();

      for (const recording of recordings) {
        const recordingDate = new Date(recording.startTime);
        if (recordingDate < cutoffDate) {
          await recording.remove();
          // Also remove associated embeddings
          await db.embeddings
            .find({
              selector: { recordingId: recording.recordingId },
            })
            .remove();
        }
      }
      console.log(
        `Cleared recordings older than ${options.olderThanDays} days`,
      );
    } else {
      // Clear everything
      await db.recordings.find().remove();
      await db.embeddings.find().remove();
      console.log('Vector database completely cleared');
    }
  } catch (error) {
    console.error('Error clearing vector database:', error);
  }
}

// Check if database needs cleanup based on size
export async function checkDatabaseHealth(): Promise<{
  needsCleanup: boolean;
  reason?: string;
  recommendation?: string;
}> {
  try {
    const stats = await getDatabaseStats();

    // Check various health metrics
    const MAX_SIZE_MB = 50;
    const MAX_TRANSCRIPTS = 150;
    const MAX_EMBEDDINGS = 5000;

    if (stats.estimatedSizeMB > MAX_SIZE_MB) {
      return {
        needsCleanup: true,
        reason: `Database size (${stats.estimatedSizeMB}MB) exceeds ${MAX_SIZE_MB}MB`,
        recommendation:
          'Consider removing old recordings or clearing transcript data',
      };
    }

    if (stats.recordingsWithTranscripts > MAX_TRANSCRIPTS) {
      return {
        needsCleanup: true,
        reason: `Too many transcripts (${stats.recordingsWithTranscripts}) stored`,
        recommendation:
          'Consider limiting transcript storage to newer recordings',
      };
    }

    if (stats.totalEmbeddings > MAX_EMBEDDINGS) {
      return {
        needsCleanup: true,
        reason: `Too many embeddings (${stats.totalEmbeddings}) in database`,
        recommendation: 'Consider reducing chunk size or limiting recordings',
      };
    }

    return { needsCleanup: false };
  } catch (error) {
    console.error('Error checking database health:', error);
    return { needsCleanup: false };
  }
}

// Export database for debugging
export async function exportDatabase() {
  const db = await getVectorDB();
  const recordings = await db.recordings.find().exec();
  const embeddings = await db.embeddings.find().exec();

  return {
    recordings: recordings.map(r => r.toJSON()),
    embeddings: embeddings.map(e => e.toJSON()),
  };
}
